class TMv_MeteoriteGlowingSky {
	/**
	 * The constructor for the class. This is called automatically in Tungsten and the element is passed in.
	 * @param {Element} element The DOM element which corresponds to the main TMv_GlowingSky component.
	 * @param {Object} options Override options that can be passed in. Not every value is an option. We differentiate internal values
	 * with values that can be overridden.
	 */

	gl = null;
	prog = null;
	resizeObserver = null;

	u = {};
	u_points = [];
	u_colors = [];
	u_age = [];
	u_speed = [];

	points = [];
	vel = [];
	colors = [];
	active = [];
	nextStartAt = [];
	age = [];
	simT = 0;

	_speedBuf = null;

	CONFIG = {
		pointCount: 12,
		activeMax: 2,

		// Motion
		speedMin: 0.5,
		speedMax: 2.5,
		driftAngleDeg: -18,
		driftBias: 0.85,

		// Blend model
		sigmaCore: 20.0,
		sigmaHalo: 6.0,
		haloStrength: 1.5,
		weightExp: 0.4,

		// Display
		exposure: 1.8,
		intensity: 0.075,
		gamma: 1.25,

		// Dither
		noiseAmount: 0.0006,
		noiseTemporal: 0,

		// Offscreen fades & lifecycle
		fadeStartPad: 0.15,
		fadeEndPad:   0.85,
		spawnPad:     1.0,
		killPad:      1.0,

		// Fade-in
		fadeInDist: 0.20,
		fadeInSec:  0.3,

		// Re-entry timing
		delayMin: 2.0,
		delayMax: 5.0,

		// Rendering
		useRAF: true,
		OVERLAY_ALPHA_GAIN: 1.2
	};

	MAX_POINTS = 64;
	CANVAS_SELECTOR = 'canvas.glowingsky_canvas';

	constructor(element, options) {
		this.element = element;
		console.log(element);
		this.canvas = this.element.querySelector(this.CANVAS_SELECTOR);
		if (!this.canvas) {
			console.warn('TMv_MeteoriteGlowingSky: canvas.glowingsky_canvas not found in element.');
			return;
		}
		if (this.CONFIG.pointCount > this.MAX_POINTS) this.CONFIG.pointCount = this.MAX_POINTS;

		this.gl = this.canvas.getContext('webgl', {
			antialias: false,
			premultipliedAlpha: true,
			alpha: true
		});
		if (!this.gl) {
			console.warn('TMv_MeteoriteGlowingSky: WebGL not available');
			return;
		}

		const parent = this.canvas.parentElement || document.body;
		this.canvas.style.width = '100%';
		this.canvas.style.height = '100%';
		this.canvas.style.display = 'block';
		this.canvas.style.background = 'transparent';

		if ('ResizeObserver' in window) {
			this.resizeObserver = new ResizeObserver(() => { this.resizeNeeded = true; });
			this.resizeObserver.observe(parent);
			this.resizeObserver.observe(this.canvas);
		}

		this.rand = this._rng(1337);

		this._initSwarm();
		this._initGL().then(() => this._start());
	}

	_rng(seed){ let t = seed>>>0; return ()=>{ t+=0x6D2B79F5; let r=Math.imul(t^(t>>>15),1|t); r^=r+Math.imul(r^(r>>>7),61|r); return ((r^(r>>>14))>>>0)/4294967296; }; }
	_randRange(a,b){ return a + (b - a) * (this.rand?.() ?? Math.random()); }
	_clamp01(x){ return Math.min(1, Math.max(0, x)); }
	_toLin(c){ return Math.pow(this._clamp01(c), 2.2); }
	_fitCanvas(){
		const dpr=Math.max(1,Math.min(2.5,window.devicePixelRatio||1));
		const r=this.canvas.getBoundingClientRect();
		const w=Math.max(1,Math.floor(r.width*dpr));
		const h=Math.max(1,Math.floor(r.height*dpr));
		if(this.canvas.width!==w||this.canvas.height!==h){this.canvas.width=w;this.canvas.height=h;}
		return {w,h,dpr};
	}
	async _load(url){ const res=await fetch(url, { cache: 'no-store' }); return await res.text(); }

	_initSwarm(){
		const n = this.CONFIG.pointCount;

		// Fixed palette
		const hex = ['4D4DFB','F06443','E59127'];
		const parseHex = (s)=> {
			s = s.toLowerCase().replace('#',''); if (s.length===3) s=s[0]+s[0]+s[1]+s[1]+s[2]+s[2];
			const r=parseInt(s.slice(0,2),16)/255, g=parseInt(s.slice(2,4),16)/255, b=parseInt(s.slice(4,6),16)/255;
			return [this._toLin(r), this._toLin(g), this._toLin(b)];
		};
		const base = hex.map(parseHex);
		this.colors = new Array(n);
		for (let i=0;i<n;i++) this.colors[i] = base[i % base.length];

		this.points = new Array(n);
		this.vel    = new Array(n);
		this.active = new Array(n).fill(false);
		this.nextStartAt = new Array(n);
		this.age = new Array(n).fill(0);
		this.simT = 0;

		const ang = this.CONFIG.driftAngleDeg * Math.PI / 180;
		const drift = { x: Math.cos(ang), y: Math.sin(ang) };

		for (let i=0;i<n;i++){
			this.points[i] = { x: 10.0, y: 10.0 };
			const speed = this._randRange(this.CONFIG.speedMin, this.CONFIG.speedMax);
			const theta = Math.atan2(drift.y, drift.x) * this.CONFIG.driftBias
				+ (2*Math.PI*(this.rand?.() ?? Math.random())) * (1 - this.CONFIG.driftBias);
			const dir = { x: Math.cos(theta), y: Math.sin(theta) };
			this.vel[i] = { x: dir.x*speed, y: dir.y*speed };
			this.nextStartAt[i] = this._randRange(0.0, this.CONFIG.delayMax);
		}

		this._speedBuf = new Float32Array(this.MAX_POINTS);
	}

	async _initGL(){
		const gl = this.gl;
		const [vs, fs] = await Promise.all([
			this._load('/admin/luscious_orange/classes/views/TMv_MeteoriteGlowingSky/shaders/vert.glsl?v=overlay2'),
			this._load('/admin/luscious_orange/classes/views/TMv_MeteoriteGlowingSky/shaders/frag.glsl?v=overlay2')
		]);

		this.prog = this._createProgram(gl, vs, fs);
		gl.useProgram(this.prog);

		const vbo = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
		gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 3,-1, -1,3]), gl.STATIC_DRAW);
		const aPos = gl.getAttribLocation(this.prog, 'a_position');
		gl.enableVertexAttribArray(aPos);
		gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

		const uNames = [
			'u_resolution','u_time','u_pointCount',
			'u_sigmaCore','u_sigmaHalo','u_haloStrength','u_weightExp',
			'u_exposure','u_intensity','u_gamma',
			'u_noiseAmount','u_noiseTemporal',
			'u_fadeStartPad','u_fadeEndPad',
			'u_fadeInSec','u_fadeInDist',
			'u_overlayAlphaGain'
		];
		uNames.forEach(n => this.u[n] = gl.getUniformLocation(this.prog, n));

		for (let i=0;i<this.MAX_POINTS;i++){
			this.u_points.push(gl.getUniformLocation(this.prog, `u_points[${i}]`));
			this.u_colors.push(gl.getUniformLocation(this.prog, `u_colors[${i}]`));
			this.u_age.push(gl.getUniformLocation(this.prog, `u_age[${i}]`));
			this.u_speed.push(gl.getUniformLocation(this.prog, `u_speed[${i}]`));
		}

		gl.uniform1i(this.u['u_pointCount'], this.CONFIG.pointCount);
		gl.uniform1f(this.u['u_sigmaCore'],    this.CONFIG.sigmaCore);
		gl.uniform1f(this.u['u_sigmaHalo'],    this.CONFIG.sigmaHalo);
		gl.uniform1f(this.u['u_haloStrength'], this.CONFIG.haloStrength);
		gl.uniform1f(this.u['u_weightExp'],    this.CONFIG.weightExp);
		gl.uniform1f(this.u['u_exposure'],     this.CONFIG.exposure);
		gl.uniform1f(this.u['u_intensity'],    this.CONFIG.intensity);
		gl.uniform1f(this.u['u_gamma'],        this.CONFIG.gamma);
		gl.uniform1f(this.u['u_noiseAmount'],  this.CONFIG.noiseAmount);
		gl.uniform1i(this.u['u_noiseTemporal'],this.CONFIG.noiseTemporal|0);
		gl.uniform1f(this.u['u_fadeStartPad'], this.CONFIG.fadeStartPad);
		gl.uniform1f(this.u['u_fadeEndPad'],   this.CONFIG.fadeEndPad);
		gl.uniform1f(this.u['u_fadeInSec'],    this.CONFIG.fadeInSec);
		gl.uniform1f(this.u['u_fadeInDist'],   this.CONFIG.fadeInDist);
		gl.uniform1f(this.u['u_overlayAlphaGain'], this.CONFIG.OVERLAY_ALPHA_GAIN);

		for (let i=0;i<this.CONFIG.pointCount;i++){
			const c = this.colors[i];
			gl.uniform3f(this.u_colors[i], c[0], c[1], c[2]);
		}

		gl.clearColor(0,0,0,0);
		gl.disable(gl.DEPTH_TEST);
		gl.disable(gl.BLEND);
	}

	_createProgram(gl, vsSource, fsSource){
		const vs = gl.createShader(gl.VERTEX_SHADER); gl.shaderSource(vs,vsSource); gl.compileShader(vs);
		if(!gl.getShaderParameter(vs,gl.COMPILE_STATUS)) throw new Error('VS: '+gl.getShaderInfoLog(vs));
		const fs = gl.createShader(gl.FRAGMENT_SHADER); gl.shaderSource(fs,fsSource); gl.compileShader(fs);
		if(!gl.getShaderParameter(fs,gl.COMPILE_STATUS)) throw new Error('FS: '+gl.getShaderInfoLog(fs));
		const p = gl.createProgram(); gl.attachShader(p,vs); gl.attachShader(p,fs);
		gl.bindAttribLocation(p,0,'a_position'); gl.linkProgram(p);
		if(!gl.getProgramParameter(p,gl.LINK_STATUS)) throw new Error('Link: '+gl.getProgramInfoLog(p));
		gl.deleteShader(vs); gl.deleteShader(fs); return p;
	}

	_spawnAtEdge(i){
		const v = this.vel[i];
		const pad = this.CONFIG.spawnPad;
		if (Math.abs(v.x) >= Math.abs(v.y)) {
			const side = (v.x > 0.0) ? -pad : 1.0 + pad;
			this.points[i].x = side;
			this.points[i].y = (this.rand?.() ?? Math.random());
		} else {
			const side = (v.y > 0.0) ? -pad : 1.0 + pad;
			this.points[i].y = side;
			this.points[i].x = (this.rand?.() ?? Math.random());
		}
		this.age[i] = 0;
	}

	_update(dt){
		this.simT += dt;

		let activeCount = 0;
		for (let i=0;i<this.CONFIG.pointCount;i++) if (this.active[i]) activeCount++;
		for (let i=0;i<this.CONFIG.pointCount && activeCount < this.CONFIG.activeMax;i++){
			if (!this.active[i] && this.simT >= this.nextStartAt[i]) {
				const speed = this._randRange(this.CONFIG.speedMin, this.CONFIG.speedMax);
				const v = this.vel[i]; const len = Math.hypot(v.x, v.y) || 1.0;
				this.vel[i].x = (v.x/len) * speed;
				this.vel[i].y = (v.y/len) * speed;
				this._spawnAtEdge(i);
				this.active[i] = true;
				activeCount++;
			}
		}

		const killNeg = -this.CONFIG.killPad, killPos = 1 + this.CONFIG.killPad;
		for (let i=0;i<this.CONFIG.pointCount;i++) {
			if (!this.active[i]) continue;
			const p=this.points[i], v=this.vel[i];
			p.x += v.x * dt; p.y += v.y * dt;
			this.age[i] += dt;
			if (p.x < killNeg || p.x > killPos || p.y < killNeg || p.y > killPos) {
				this.active[i] = false;
				this.nextStartAt[i] = this.simT + this._randRange(this.CONFIG.delayMin, this.CONFIG.delayMax);
				this.points[i].x = 10.0; this.points[i].y = 10.0; this.age[i] = 0;
			}
		}
	}

	_render(time){
		const gl = this.gl;
		const {w,h} = this._fitCanvas();
		gl.viewport(0,0,w,h);
		gl.useProgram(this.prog);
		gl.clear(gl.COLOR_BUFFER_BIT);
		gl.uniform2f(this.u['u_resolution'], w, h);
		gl.uniform1f(this.u['u_time'], time);

		for (let i=0;i<this.CONFIG.pointCount;i++){
			const p=this.points[i];
			gl.uniform2f(this.u_points[i], p.x, p.y);
			gl.uniform1f(this.u_age[i], this.age[i]);
			const v=this.vel[i]; const s=Math.hypot(v.x, v.y); this._speedBuf[i]=s;
			gl.uniform1f(this.u_speed[i], s);
		}
		for (let i=this.CONFIG.pointCount;i<this.MAX_POINTS;i++){
			gl.uniform2f(this.u_points[i], 10.0, 10.0);
			gl.uniform1f(this.u_age[i], 0.0);
			gl.uniform1f(this.u_speed[i], 0.0);
		}

		gl.drawArrays(gl.TRIANGLES, 0, 3);
	}

	_frame(ts){
		if (!this.running) return;
		const t = ts/1000;
		if (this._tLast == null) this._tLast = t;
		const dt = Math.min(0.05, t - this._tLast); this._tLast = t;

		if (this.resizeNeeded) { this._fitCanvas(); this.resizeNeeded = false; }
		this._update(dt); this._render(t - (this._t0 ?? t)); if (this._t0==null) this._t0 = t;

		if (this.CONFIG.useRAF) requestAnimationFrame(this._frame.bind(this));
	}

	_start(){ if (this.running) return; this.running = true; requestAnimationFrame(this._frame.bind(this)); }
	stop(){ this.running = false; }
}
